import React from 'react'
import { Button, Popconfirm, message } from 'antd'
import { useUpdate, useInvalidate } from '@refinedev/core'

/**
 * RevokeAdminButton — demote an admin back to member from the Accounts & Admins
 * roster. Sets profiles.role='member' for the target; the admin login gate
 * checks role==='admin', so the revoked user immediately loses admin-panel
 * access. Admin-gated by the same `profiles: admin update` RLS policy used by
 * the promote flow — no Edge Function needed.
 *
 * Rendered only for OTHER admins' rows (never the current user — see list.tsx),
 * so an admin can't lock themselves out, which also guarantees the last admin
 * (yourself) can never be revoked.
 */
export function RevokeAdminButton({ userId, name }: { userId: string; name: string | null }) {
  const [busy, setBusy] = React.useState(false)
  const { mutate: update } = useUpdate()
  const invalidate = useInvalidate()

  function revoke() {
    setBusy(true)
    update(
      {
        resource: 'profiles',
        id: userId,
        meta: { idColumnName: 'user_id' },
        values: { role: 'member' },
        successNotification: false,
        errorNotification: false,
        mutationMode: 'pessimistic',
      },
      {
        onSuccess: () => {
          message.success(`${name ?? 'This member'} no longer has admin access.`)
          invalidate({ resource: 'admin_users', invalidates: ['list'] })
        },
        onError: (e: unknown) =>
          message.error(`Could not revoke: ${e instanceof Error ? e.message : 'unknown error'}`),
        onSettled: () => setBusy(false),
      },
    )
  }

  return (
    <Popconfirm
      title="Revoke admin access?"
      description={`${name ?? 'This member'} will lose access to the admin panel.`}
      okText="Yes, revoke admin"
      cancelText="Cancel"
      okButtonProps={{ danger: true }}
      onConfirm={revoke}
    >
      <Button size="small" danger loading={busy} data-testid={`revoke-admin-${userId}`}>
        Revoke admin
      </Button>
    </Popconfirm>
  )
}
